/**
 * المرحلة 1.35 — the children staying in the centre, as tiles: the child, the
 * unit, the day of the stay, the doctor; for those who see money, what
 * remains and what waits for review. A tile opens the stay: the booklet for
 * the nurse and the doctor (`StayBookletBoard`), the account for accounts
 * (features/accounts/StayAccounts.jsx).
 *
 * Shown under the `stayBooklet` policy only; the server answers 403
 * STAY_BOOKLET_CLOSED otherwise.
 */

import { useCallback, useEffect, useState } from 'react';
import { Button, EmptyState, ErrorState, Icon, Modal, Spinner } from '../../components/common';
import { staysApi } from '../../api/endpoints';
import { usePermissions } from '../../context/PermissionContext';
import { useI18n } from '../../i18n/I18nProvider';
import { usePoll } from '../../hooks/usePolling';
import { activeCountry, formatDate, formatMoney } from '../../domain/locale';
import { ageLabel } from '../../domain/childFile';
import { admits, dayLabel, leftOf, stayDay } from '../../domain/stays';
import { AdmitDialog, DischargeBox } from './StayAdmission';
import { StayBooklet } from './StayBooklet';

/** One stay's tile. */
function StayTile({ row, money, onOpen, active }) {
  const { t } = useI18n();
  const day = row.status === 'open' ? stayDay(row.admittedAt, new Date(), activeCountry().timezone) : null;
  const girl = row.patient?.gender === 'female';
  const left = leftOf(row.totals?.remaining);
  return (
    <button type="button" className={`sb-tile${girl ? ' sb-tile--girl' : ''}${active ? ' sb-tile--active' : ''}`} onClick={() => onOpen(row)} data-testid="stay-tile">
      <span className="sb-tile__top">
        <span className="sb-tile__face" aria-hidden="true">
          <Icon name="baby" size={20} />
        </span>
        <span className="sb-tile__name">{row.patient?.fullName || `#${row.hospitalPatientId}`}</span>
        {day ? <span className="sb-chip sb-chip--day">{dayLabel(day, t)}</span> : <span className="sb-chip">{t(`stay.discharge.kind.${row.dischargeKind}`)}</span>}
      </span>
      <span className="sb-tile__meta">
        {row.patient?.dateOfBirth && <span>{t('stay.banner.age', { age: ageLabel(row.patient.dateOfBirth, t) })}</span>}
        {row.unitLabel && <span>{row.unitLabel}</span>}
        {row.attendingDoctorName && <span>{row.attendingDoctorName}</span>}
        <span>{t('stay.banner.admitted', { date: formatDate(row.admittedAt) })}</span>
      </span>
      {row.diagnosis && <span className="sb-tile__diagnosis">{row.diagnosis}</span>}
      {money && row.totals && (
        <span className="sb-tile__money">
          <span>
            {t('stay.board.total')}: <b>{formatMoney(row.totals.total)}</b>
          </span>
          <span className={left.due ? 'sb-tile__due' : 'sb-tile__paid'}>
            {t(left.credit ? 'stay.board.credit' : 'stay.board.remaining')}: <b>{formatMoney(left.amount)}</b>
          </span>
          {row.totals.pending > 0 && <span className="sb-tile__wait">{t('stay.board.pending', { amount: formatMoney(row.totals.pending) })}</span>}
          {row.clearedAt && row.status === 'open' && (
            <span className="sb-tile__ok">
              <Icon name="badge-check" size={14} /> {t('stay.banner.cleared')}
            </span>
          )}
        </span>
      )}
    </button>
  );
}

/**
 * @param {{ mode: 'nursing'|'doctor'|'accounts', onOpen: (row) => void, activeId?: number, refreshKey?: number }} props
 */
export function StaysBoard({ mode, onOpen, activeId, refreshKey = 0 }) {
  const { t } = useI18n();
  const { can } = usePermissions();
  const [status, setStatus] = useState('open');
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [admitting, setAdmitting] = useState(false);
  const load = useCallback(
    () =>
      staysApi.list({ status }).then((r) => {
        setRows(r.stays);
        setError(null);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [status, refreshKey],
  );
  // A new filter (or a change made elsewhere) reloads now; the poll keeps it fresh.
  useEffect(() => {
    setRows(null);
    load().catch(setError);
  }, [load]);
  usePoll(load, 30000, { immediate: false, onError: (err) => setError((prev) => prev || err) });

  if (error && !rows) return <ErrorState error={error} onRetry={() => load().catch(setError)} />;
  const money = mode === 'accounts';
  return (
    <div className="space-y-4" data-testid={`stays-board-${mode}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('stay.board.title')}>
          {['open', 'discharged'].map((s) => (
            <button key={s} type="button" role="radio" aria-checked={status === s} className={`sb-filter${status === s ? ' sb-filter--on' : ''}`} onClick={() => setStatus(s)}>
              <Icon name={s === 'open' ? 'bed-double' : 'log-out'} size={15} /> {t(`stay.board.${s}`)}
            </button>
          ))}
        </div>
        {mode !== 'nursing' && admits(can) && (
          <Button icon="plus" onClick={() => setAdmitting(true)} data-testid="stay-admit-open">
            {t('stay.board.admit')}
          </Button>
        )}
      </div>
      {!rows ? (
        <Spinner label={t('stay.loading')} variant="inline" />
      ) : rows.length === 0 ? (
        <EmptyState icon="bed-double" title={status === 'open' ? t('stay.board.empty') : t('stay.board.emptyOut')} message={status === 'open' ? t('stay.board.emptyHint') : undefined} />
      ) : (
        <div className="sb-tiles">
          {rows.map((row) => (
            <StayTile key={row.id} row={row} money={money} onOpen={onOpen} active={activeId === row.id} />
          ))}
        </div>
      )}
      {admitting && (
        <AdmitDialog
          onClose={() => setAdmitting(false)}
          onDone={(fresh) => {
            setAdmitting(false);
            load().catch(() => {});
            onOpen({ ...fresh.stay, patient: fresh.patient });
          }}
        />
      )}
    </div>
  );
}

/** The nurse's and the doctor's board: a tile opens the stay's booklet, with its discharge under it. */
export function StayBookletBoard({ mode = 'nursing' }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(null);
  const [data, setData] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [bookletKey, setBookletKey] = useState(0);
  return (
    <>
      <StaysBoard mode={mode} onOpen={setOpen} activeId={open?.id} refreshKey={refreshKey} />
      {open && (
        <Modal
          open
          onClose={() => {
            setOpen(null);
            setData(null);
            setRefreshKey((k) => k + 1);
          }}
          title={open.patient?.fullName || t('stay.booklet')}
          icon="bed-double"
          size="xl"
        >
          <StayBooklet key={bookletKey} stayId={open.id} defaultPage={mode === 'doctor' ? 'orders' : 'care'} onChanged={setData} />
          {mode === 'doctor' && data && (
            <DischargeBox
              data={data}
              onChange={(fresh) => {
                setData(fresh);
                setBookletKey((k) => k + 1);
              }}
            />
          )}
        </Modal>
      )}
    </>
  );
}

export default StaysBoard;
